import React, { ReactNode, useCallback, useMemo } from 'react';
import { useAuxCodeContext } from './context';
import { StyledDropdownOption } from '../../../../components/StyledComponents';
import StyledDropdown from '../../../../components/StyledComponents/StyledDropdown';
import styles from '../../../../components/navigation/styles.module.scss';
import {
    selectAllCalls,
    selectCurrentUserId,
    selectCurrentUserQueueAvailability,
    selectCurrentUserSipName,
    selectQueueAvailability,
    selectUserAuxCode,
    selectUserGlobalDnd
} from '../../../../redux/slices';
import { useTypedSelector } from '../../../../redux/hooks';
import {
    usePutAuxCodeMutation,
    usePutAvailableInCallQueuesMutation
} from '../../../../redux/services/sipApi';
import { authApi } from '../../../../redux/services/authApi';

interface Props {
    user_data?: ForOtherProps;
}

export const auxCodes = [
    'Available',
    'Unavailable',
    'Do Not Disturb',
    'After Call Work',
    'Lunch',
    'Break',
    'Scheduled Time Off',
    'Unscheduled Time Off',
    'Callout',
    'Supervisor Meeting',
    'Tech Issue',
    'System Startup'
];

const availableReason: StyledDropdownOption = {
    label: 'Available',
    value: 'Available'
};

const defaultPauseReasons: StyledDropdownOption[] = [
    {
        label: 'Unavailable',
        value: 'Unavailable'
    }
];

interface DropdownProps {
    display: string | ReactNode | undefined;
    handleAuxSelect: (val: string) => void;
    loading: boolean;
    selected: string | undefined;
    disabled?: boolean;
}

interface ForOtherProps {
    user_uuid: string;
    user_name: string;
}

const useChangePauseReason = () => {
    const { useAuxAction } = useAuxCodeContext();

    const currentUserUuid = useTypedSelector(selectCurrentUserId);
    const callsLength = useTypedSelector(state => selectAllCalls(state)?.length || 0);

    const [putAvailableInCallQueues, { isLoading: baseLoading }] =
        usePutAvailableInCallQueuesMutation();
    const [putAuxCode, { isLoading: auxLoading }] = usePutAuxCodeMutation();

    const handleChangePause = useCallback(
        (pauseReason: string, queueAvailability: boolean, userUuid?: string) => {
            if (!userUuid && callsLength > 0 && pauseReason !== 'Available') {
                useAuxAction({
                    type: 'set_change_after_call_code',
                    payload: pauseReason
                });
                return;
            }

            const voipUser = userUuid || currentUserUuid;

            putAuxCode({
                voip_user_uuid: voipUser,
                auxCode: {
                    code: pauseReason,
                    set_by: currentUserUuid
                }
            });

            if (pauseReason === 'Available' && !queueAvailability) {
                putAvailableInCallQueues({
                    voip_user_uuid: voipUser,
                    available: true
                });
            }

        },
        [callsLength, currentUserUuid]
    );

    return {
        auxLoading: auxLoading || baseLoading,
        handleChangePause
    };
};

const ControllerDropdown: React.FC<DropdownProps> = ({
    display,
    handleAuxSelect,
    loading,
    selected = 'Available',
    disabled
}) => {
    const { data: pauseConfig } = authApi.endpoints.getPauseConfig.useQuery();

    const normalisedSelected = selected === 'available' ? 'Available' : selected;

    const opts: StyledDropdownOption[] = useMemo(() => {
        const reasons: StyledDropdownOption[] = pauseConfig?.pause_allowed
            ? pauseConfig.pause_reasons
                .filter(pr => pr.active)
                .map(pr => ({ label: pr.pause_reason, value: pr.pause_reason }))
            : defaultPauseReasons;

        const isKnown =
            normalisedSelected === availableReason.value ||
            reasons.some(r => r.value === normalisedSelected);

        const resList: StyledDropdownOption[] = [availableReason];

        if (!isKnown) {
            resList.push({ label: normalisedSelected, value: normalisedSelected, disabled: true })
        };

        return resList.concat(reasons);
    }, [pauseConfig, normalisedSelected]);

    return (
        <StyledDropdown
            display={display}
            options={opts}
            onSelect={handleAuxSelect}
            loading={loading}
            selected={normalisedSelected}
            className={styles.dropdown}
            disabled={disabled}
        />
    );
};

const ControllerForSelf: React.FC = () => {
    const { code_to_change_to } = useAuxCodeContext();

    const queueAvailability = useTypedSelector(selectCurrentUserQueueAvailability);
    const currentUserSipName = useTypedSelector(selectCurrentUserSipName);

    const aux_code = useTypedSelector(state => selectUserAuxCode(state, currentUserSipName));
    const global_dnd = useTypedSelector(state => selectUserGlobalDnd(state, currentUserSipName));

    const { auxLoading, handleChangePause } = useChangePauseReason();

    return (
        <ControllerDropdown
            display={code_to_change_to ? <p>Change After Call</p> : undefined}
            handleAuxSelect={(val: string) => handleChangePause(val, queueAvailability)}
            loading={auxLoading || false}
            selected={aux_code}
            disabled={global_dnd}
        />
    );
};

const ControllerForOther: React.FC<ForOtherProps> = ({ user_uuid, user_name }) => {
    const aux_code = useTypedSelector(state => selectUserAuxCode(state, user_name));
    const queueAvailability = useTypedSelector(state => selectQueueAvailability(state, user_name));

    const { auxLoading, handleChangePause } = useChangePauseReason();

    return (
        <ControllerDropdown
            display={undefined}
            handleAuxSelect={(val: string) => handleChangePause(val, queueAvailability, user_uuid)}
            loading={auxLoading || false}
            selected={aux_code}
        />
    );
};

const AuxCodeController: React.FC<Props> = ({ user_data }) => {
    if (user_data) {
        return (
            <ControllerForOther user_uuid={user_data.user_uuid} user_name={user_data.user_name} />
        );
    }

    return <ControllerForSelf />;
};

export default AuxCodeController;
